import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  Button,
  SpaceBetween,
  Container,
  Form,
  FormField,
  Input,
  Select,
  Textarea,
  Alert,
  Box
} from '@cloudscape-design/components';
import { titleAPI } from '../services/api';
import ParentSelector from '../components/ParentSelector';

const CreateTitle = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get('parentId');
  const parentType = searchParams.get('parentType');
  const childType = searchParams.get('childType');
  const parentTitleName = searchParams.get('parentTitle');

  const [loading, setLoading] = useState(false);
  const [showParentSelector, setShowParentSelector] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    content_type: childType || 'film',
    ...(parentId ? { parent_id: parentId } : {}),
    metadata: {
      title: '',
      year: '',
      genre: [],
      synopsis: '',
      language: 'en',
      country: 'US',
      status: 'draft'
    },
    external_ids: {},
    reference_ids: {}
  });

  const contentTypes = [
    { label: 'Film', value: 'film' },
    { label: 'Series', value: 'series' },
    { label: 'Season', value: 'season' },
    { label: 'Episode', value: 'episode' },
    { label: 'Documentary', value: 'documentary' },
    { label: 'Short', value: 'short' },
    { label: 'Special', value: 'special' }
  ];

  const statusOptions = [
    { label: 'Draft', value: 'draft' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Review', value: 'review' },
    { label: 'Approved', value: 'approved' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Archived', value: 'archived' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Clean up form data
      const submitData = {
        ...formData,
        metadata: {
          ...formData.metadata,
          year: formData.metadata.year ? parseInt(formData.metadata.year) : undefined,
          genre: formData.metadata.genre.filter(g => g.trim())
        }
      };

      // Remove empty objects
      if (Object.keys(submitData.external_ids).length === 0) {
        delete submitData.external_ids;
      }
      if (Object.keys(submitData.reference_ids).length === 0) {
        delete submitData.reference_ids;
      }

      const response = await titleAPI.createTitle(submitData);
      navigate(`/titles/${response.data.title_id}`);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create title');
    } finally {
      setLoading(false);
    }
  };

  const updateMetadata = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value
      }
    }));
  };

  const updateExternalId = (system, value) => {
    setFormData(prev => ({
      ...prev,
      external_ids: {
        ...prev.external_ids,
        [system]: value
      }
    }));
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <Button onClick={() => navigate(parentId ? `/titles/${parentId}` : '/')}>
              Cancel
            </Button>
          }
        >
          {parentTitleName 
            ? `Create ${childType ? childType.charAt(0).toUpperCase() + childType.slice(1) : 'Title'} — ${parentTitleName}`
            : 'Create New Title'
          }
        </Header>
      }
    >
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => navigate(parentId ? `/titles/${parentId}` : '/')}>
                Cancel
              </Button>
              <Button variant="primary" loading={loading} type="submit">
                Create Title
              </Button>
            </SpaceBetween>
          }
        >
          <Container header={<Box variant="h3">Basic Information</Box>}>
            <SpaceBetween size="m">
              <FormField label="Content Type" stretch>
                <Select
                  selectedOption={contentTypes.find(t => t.value === formData.content_type)}
                  onChange={({ detail }) => setFormData(prev => ({ ...prev, content_type: detail.selectedOption.value }))}
                  options={contentTypes}
                />
              </FormField>

              <FormField label="Title" stretch>
                <Input
                  value={formData.metadata.title}
                  onChange={({ detail }) => updateMetadata('title', detail.value)}
                  placeholder="Enter title"
                />
              </FormField>

              <FormField label="Year" stretch>
                <Input
                  value={formData.metadata.year}
                  onChange={({ detail }) => updateMetadata('year', detail.value)}
                  placeholder="2024"
                  type="number"
                />
              </FormField>

              <FormField label="Genre (comma-separated)" stretch>
                <Input
                  value={formData.metadata.genre.join(', ')}
                  onChange={({ detail }) => updateMetadata('genre', detail.value.split(',').map(g => g.trim()))}
                  placeholder="drama, action, comedy"
                />
              </FormField>

              <FormField label="Synopsis" stretch>
                <Textarea
                  value={formData.metadata.synopsis}
                  onChange={({ detail }) => updateMetadata('synopsis', detail.value)}
                  placeholder="Enter synopsis"
                  rows={4}
                />
              </FormField>

              <FormField label="Language" stretch>
                <Input
                  value={formData.metadata.language}
                  onChange={({ detail }) => updateMetadata('language', detail.value)}
                  placeholder="en"
                />
              </FormField>

              <FormField label="Country" stretch>
                <Input
                  value={formData.metadata.country}
                  onChange={({ detail }) => updateMetadata('country', detail.value)}
                  placeholder="US"
                />
              </FormField>

              <FormField label="Status" stretch>
                <Select
                  selectedOption={statusOptions.find(s => s.value === formData.metadata.status)}
                  onChange={({ detail }) => updateMetadata('status', detail.selectedOption.value)}
                  options={statusOptions}
                />
              </FormField>

              {(formData.content_type === 'season' || formData.content_type === 'episode') && (
                <FormField 
                  label="Parent ID" 
                  description="ID of the parent (series for season, season for episode)" 
                  stretch
                  secondaryControl={
                    <Button 
                      variant="normal"
                      onClick={() => setShowParentSelector(true)}
                    >
                      Browse Parents
                    </Button>
                  }
                >
                  <Input
                    value={formData.parent_id || ''}
                    onChange={({ detail }) => setFormData(prev => ({ ...prev, parent_id: detail.value }))}
                    placeholder="Enter parent ID or use Browse"
                  />
                </FormField>
              )}

              {formData.content_type === 'season' && (
                <FormField label="Season Number" stretch>
                  <Input
                    value={formData.metadata.season_number || ''}
                    onChange={({ detail }) => updateMetadata('season_number', parseInt(detail.value) || undefined)}
                    placeholder="1"
                    type="number"
                  />
                </FormField>
              )}

              {formData.content_type === 'episode' && (
                <>
                  <FormField label="Season Number" stretch>
                    <Input
                      value={formData.metadata.season_number || ''}
                      onChange={({ detail }) => updateMetadata('season_number', parseInt(detail.value) || undefined)}
                      placeholder="1"
                      type="number"
                    />
                  </FormField>
                  <FormField label="Episode Number" stretch>
                    <Input
                      value={formData.metadata.episode_number || ''}
                      onChange={({ detail }) => updateMetadata('episode_number', parseInt(detail.value) || undefined)}
                      placeholder="1"
                      type="number"
                    />
                  </FormField>
                </>
              )}
            </SpaceBetween>
          </Container>

          <Container header={<Box variant="h3">External IDs (Optional)</Box>}>
            <SpaceBetween size="m">
              <FormField label="Dalet ID" stretch>
                <Input
                  value={formData.external_ids.dalet_id || ''}
                  onChange={({ detail }) => updateExternalId('dalet_id', detail.value)}
                  placeholder="DAL123456"
                />
              </FormField>

              <FormField label="Avid ID" stretch>
                <Input
                  value={formData.external_ids.avid_id || ''}
                  onChange={({ detail }) => updateExternalId('avid_id', detail.value)}
                  placeholder="AVID789"
                />
              </FormField>

              <FormField label="Adobe ID" stretch>
                <Input
                  value={formData.external_ids.adobe_id || ''}
                  onChange={({ detail }) => updateExternalId('adobe_id', detail.value)}
                  placeholder="ADOBE456"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container header={<Box variant="h3">Reference IDs (Optional)</Box>}>
            <SpaceBetween size="m">
              <FormField label={<span style={{textTransform: 'none'}}>IMDb ID</span>} stretch>
                <Input
                  value={formData.reference_ids.imdb_id || ''}
                  onChange={({ detail }) => setFormData(prev => ({
                    ...prev,
                    reference_ids: { ...prev.reference_ids, imdb_id: detail.value }
                  }))}
                  placeholder="tt1234567"
                />
              </FormField>

              <FormField label="TMDB ID" stretch>
                <Input
                  value={formData.reference_ids.tmdb_id || ''}
                  onChange={({ detail }) => setFormData(prev => ({
                    ...prev,
                    reference_ids: { ...prev.reference_ids, tmdb_id: detail.value }
                  }))}
                  placeholder="12345"
                />
              </FormField>
            </SpaceBetween>
          </Container>
        </Form>
      </form>
      
      <ParentSelector
        visible={showParentSelector}
        onDismiss={() => setShowParentSelector(false)}
        onSelect={(parent) => {
          setFormData(prev => ({ ...prev, parent_id: parent.title_id }));
        }}
        contentType={formData.content_type}
      />
    </ContentLayout>
  );
};

export default CreateTitle;